import classNames from 'classnames';
import React from 'react';
import type { AutoNoteStrategy } from 'types/hour';
import AutofillBar from './AutofillBar';
import styles from './NoteSection.module.scss';
import { Section } from './Section';

interface NoteSectionProps {
  note: string;
  disabled?: boolean;
  disableAutofill?: boolean;
  autoFillTooltip?: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => unknown;
  standalone?: boolean;
  onSubmit?: (event: React.KeyboardEvent<unknown>) => unknown;
  autoNoteStrategy?: AutoNoteStrategy;
  defaultAutoNoteStrategy?: AutoNoteStrategy;
  onAutoNoteStrategyChange: (strategy: AutoNoteStrategy, setAsDefault?: boolean) => unknown;
  placeholder?: string;
  required: boolean;
  autoFocus?: boolean;
  noBorder?: boolean;
  hasSelectedProject?: boolean;
  hideAutofill?: boolean;
  hasEnabledTic?: boolean;
  timestampsHaveMemories?: boolean;
}

const NoteSection: React.FC<NoteSectionProps> = ({
  note,
  autoNoteStrategy,
  defaultAutoNoteStrategy,
  onSubmit,
  onChange,
  onAutoNoteStrategyChange,
  disabled,
  disableAutofill,
  autoFillTooltip,
  placeholder = 'Add a note for this work',
  standalone,
  required,
  autoFocus,
  noBorder,
  hideAutofill,
  hasEnabledTic,
  timestampsHaveMemories,
}) => {
  // Submit on ENTER, linebreaks with SHIFT + ENTER
  function handleInputKeyDown(event: React.KeyboardEvent<unknown>) {
    const isTypingJapaneseCharacter = event.keyCode === 229;

    if (event.key === 'Enter' && !event.shiftKey && !isTypingJapaneseCharacter) {
      event.preventDefault();
      if (onSubmit) {
        onSubmit(event);
      }
    }

    if (event.key === 'Escape' && isTypingJapaneseCharacter) {
      event.stopPropagation();
    }
  }

  return (
    <Section noBorder={noBorder} standalone={standalone} className={styles.section}>
      <div
        className={classNames(styles.container, { [styles.disabled]: disabled })}
        data-value={note}
      >
        <textarea
          required={required}
          className={classNames('fs-mask', styles.textarea)}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          onChange={onChange}
          value={note}
          onKeyDown={onSubmit ? handleInputKeyDown : undefined}
          rows={1}
          data-testid="note_field"
        ></textarea>
      </div>
      {!hideAutofill && (
        <AutofillBar
          currentStrategy={autoNoteStrategy}
          defaultStrategy={defaultAutoNoteStrategy}
          onChange={onAutoNoteStrategyChange}
          disableAutofill={disableAutofill}
          tooltip={autoFillTooltip}
          hasEnabledTic={hasEnabledTic}
          timestampsHaveMemories={timestampsHaveMemories}
        />
      )}
    </Section>
  );
};

export default NoteSection;
